import { BASE_36_RADIX } from '../constants';
import { existsSync } from 'node:fs';
import { mkdir, stat } from 'node:fs/promises';
import {
	dirname,
	join,
	basename,
	extname,
	resolve,
	relative,
	sep
} from 'node:path';
import { env } from 'node:process';
import { write, file, Transpiler } from 'bun';
import { resolvePackageImport } from './resolvePackageImport';
import { getFrameworkGeneratedDir } from '../utils/generatedDir';
import { buildIslandMetadataExports } from '../islands/sourceMetadata';
import { createSvelteStylePreprocessor } from './stylePreprocessor';
import { lowerSvelteIslandSyntax } from '../svelte/lowerIslandSyntax';
import { lowerSvelteAwaitSlotSyntax } from '../svelte/lowerAwaitSlotSyntax';
import { SVELTE_PAGE_ROOT_ID } from '../svelte/renderToReadableStream';
import type { StylePreprocessorConfig } from '../../types/build';
const resolveDevClientDir = () => {
	const projectRoot = process.cwd();
	const fromSource = resolve(import.meta.dir, '../dev/client');

	if (existsSync(fromSource) && fromSource.startsWith(projectRoot)) {
		return fromSource;
	}

	const fromNodeModules = resolve(
		projectRoot,
		'node_modules/@absolutejs/absolute/dist/dev/client'
	);
	if (existsSync(fromNodeModules)) return fromNodeModules;

	return resolve(import.meta.dir, './dev/client');
};

const devClientDir = resolveDevClientDir();

const hmrClientPath = join(devClientDir, 'hmrClient.ts').replace(/\\/g, '/');

type Built = {
	ssr: string;
	client: string;
	hasAwaitSlot: boolean;
	/** `.svelte.ts`/`.svelte.js` runes module (compileModule output) — has no
	 *  component default export, so it must never get a hydration index. */
	isModule: boolean;
};
type Cache = Map<string, Built>;

// Persistent cache across HMR cycles — avoids recompiling unchanged Svelte components
const persistentCache: Cache = new Map();

// Content hash cache to detect unchanged source files
const sourceHashCache = new Map<string, string>();

export const clearSvelteCompilerCache = () => {
	persistentCache.clear();
	sourceHashCache.clear();
};

const transpiler = new Transpiler({ loader: 'ts', target: 'browser' });

const removeUnusedRequireHelper = (code: string) => {
	const helperStart = code.indexOf('var __require = /* @__PURE__ */');
	if (helperStart === -1) {
		return code;
	}

	const helperEndMarker =
		"throw Error('Dynamic require of \"' + x + '\" is not supported');";
	const helperEnd = code.indexOf(helperEndMarker, helperStart);
	if (helperEnd === -1) {
		return code;
	}

	const statementEnd = code.indexOf('});', helperEnd);
	if (statementEnd === -1) {
		return code;
	}

	const stripped = `${code.slice(0, helperStart)}${code.slice(statementEnd + 3)}`;

	return /\b__require\b/.test(stripped) ? code : stripped;
};

const exists = async (path: string) => {
	try {
		await stat(path);

		return true;
	} catch {
		return false;
	}
};

const resolveRelativeModule = async (spec: string, from: string) => {
	if (!spec.startsWith('.')) return null;

	const basePath = resolve(dirname(from), spec);
	const candidates = [
		basePath,
		`${basePath}.ts`,
		`${basePath}.js`,
		`${basePath}.mjs`,
		`${basePath}.cjs`,
		`${basePath}.json`,
		`${basePath}.svelte`,
		`${basePath}.svelte.ts`,
		`${basePath}.svelte.js`,
		join(basePath, 'index.ts'),
		join(basePath, 'index.js'),
		join(basePath, 'index.mjs'),
		join(basePath, 'index.cjs'),
		join(basePath, 'index.json'),
		join(basePath, 'index.svelte'),
		join(basePath, 'index.svelte.ts'),
		join(basePath, 'index.svelte.js')
	];

	const checks = await Promise.all(candidates.map(exists));

	return candidates.find((_, index) => checks[index]) ?? null;
};

const resolveSvelte = async (spec: string, from: string) => {
	// Try bare module imports (e.g. "@absolutejs/absolute/svelte/components/Image.svelte")
	if (!spec.startsWith('.') && !spec.startsWith('/')) {
		const resolved = resolvePackageImport(spec);

		return resolved && /\.svelte(\.(?:ts|js))?$/.test(resolved)
			? resolved
			: null;
	}

	const basePath = resolve(dirname(from), spec);
	const explicit = /\.(svelte|svelte\.(?:ts|js))$/.test(basePath);

	if (!explicit) {
		const extensions = ['.svelte', '.svelte.ts', '.svelte.js'];
		const paths = extensions.map((ext) => `${basePath}${ext}`);
		const checks = await Promise.all(paths.map(exists));
		const match = paths.find((_, index) => checks[index]);

		return match ?? null;
	}

	if (await exists(basePath)) return basePath;
	if (!basePath.endsWith('.svelte')) return null;

	const tsPath = `${basePath}.ts`;
	if (await exists(tsPath)) return tsPath;

	const jsPath = `${basePath}.js`;
	if (await exists(jsPath)) return jsPath;

	return null;
};

const addModuleRewrite = (
	rewrites: Map<string, { server: string; client: string }>,
	rawSpec: string,
	resolvedModule: string,
	ssrOutputDir: string,
	clientOutputDir: string
) => {
	const toServer = relative(ssrOutputDir, resolvedModule).replace(/\\/g, '/');
	const toClient = relative(clientOutputDir, resolvedModule).replace(
		/\\/g,
		'/'
	);

	rewrites.set(rawSpec, {
		client:
			toClient.startsWith('.') || toClient.startsWith('/')
				? toClient
				: `./${toClient}`,
		server: toServer.startsWith('.') ? toServer : `./${toServer}`
	});
};

export const compileSvelte = async (
	entryPoints: string[],
	svelteRoot: string,
	cache: Cache = new Map(),
	isDev = false,
	stylePreprocessors?: StylePreprocessorConfig
) => {
	const { compile, compileModule, preprocess } = await import(
		'svelte/compiler'
	);

	// Generated output lives at <projectRoot>/.absolutejs/generated/svelte/.
	// See `src/utils/generatedDir.ts` for rationale (keeps `src/` clean).
	const generatedDir = getFrameworkGeneratedDir('svelte');
	const clientDir = join(generatedDir, 'client');
	const indexDir = join(generatedDir, 'indexes');
	const serverDir = join(generatedDir, 'server');

	await Promise.all(
		[clientDir, indexDir, serverDir].map((dir) =>
			mkdir(dir, { recursive: true })
		)
	);

	const dev = env.NODE_ENV !== 'production';

	const build = async (src: string) => {
		const memoized = cache.get(src);
		if (memoized) return memoized;

		const raw = await file(src).text();
		const islandMetadataExports = buildIslandMetadataExports(raw);

		// Check if source is unchanged since last compilation. Disk-check
		// the cached outputs too: an external process can wipe
		// `.absolutejs/generated` while our in-memory cache still claims
		// to have written there, and a later bundle pass would then
		// fail to resolve the missing component.
		const contentHash = Bun.hash(raw).toString(BASE_36_RADIX);
		const prevHash = sourceHashCache.get(src);
		const persistent = persistentCache.get(src);

		if (
			prevHash === contentHash &&
			persistent &&
			existsSync(persistent.ssr) &&
			existsSync(persistent.client)
		) {
			cache.set(src, persistent);

			return persistent;
		}

		sourceHashCache.set(src, contentHash);
		const isModule =
			src.endsWith('.svelte.ts') || src.endsWith('.svelte.js');
		const loweredAwaitServerSource = isModule
			? { code: raw, transformed: false }
			: lowerSvelteAwaitSlotSyntax(raw);
		const loweredAwaitClientSource = isModule
			? loweredAwaitServerSource
			: lowerSvelteAwaitSlotSyntax(raw);
		const loweredServerSource = isModule
			? loweredAwaitServerSource
			: lowerSvelteIslandSyntax(loweredAwaitServerSource.code, 'server');
		const loweredClientSource = isModule
			? loweredAwaitClientSource
			: lowerSvelteIslandSyntax(loweredAwaitClientSource.code, 'client');
		const transformedByLowering =
			loweredAwaitServerSource.transformed ||
			loweredAwaitClientSource.transformed ||
			loweredServerSource.transformed ||
			loweredClientSource.transformed;
		const svelteStylePreprocessor =
			createSvelteStylePreprocessor(stylePreprocessors);
		const preprocessedServer = isModule
			? loweredServerSource.code
			: (
					await preprocess(
						loweredServerSource.code,
						svelteStylePreprocessor
					)
				).code;
		const preprocessedClient = isModule
			? loweredClientSource.code
			: (
					await preprocess(
						loweredClientSource.code,
						svelteStylePreprocessor
					)
				).code;
		const transpiledServer =
			src.endsWith('.ts') || src.endsWith('.svelte.ts')
				? transpiler.transformSync(preprocessedServer)
				: preprocessedServer;
		const transpiledClient =
			src.endsWith('.ts') || src.endsWith('.svelte.ts')
				? transpiler.transformSync(preprocessedClient)
				: preprocessedClient;

		const rawRel = dirname(relative(svelteRoot, src)).replace(/\\/g, '/');
		// When a source file lives outside svelteRoot (e.g. src/svelte/components/Head.svelte
		// imported from example/svelte/pages/), the relative path starts with "../".
		// Use cwd-relative path so compiled output stays inside generated/.
		const relDir = rawRel.startsWith('..')
			? `_ext/${relative(process.cwd(), dirname(src)).replace(/\\/g, '/')}`
			: rawRel;
		const baseName = basename(src).replace(/\.svelte(\.(ts|js))?$/, '');

		const importPaths = Array.from(
			transpiledServer.matchAll(/from\s+['"]([^'"]+)['"]/g)
		)
			.map((match) => match[1])
			.filter((path): path is string => path !== undefined);

		const resolvedModuleImports = await Promise.all(
			importPaths.map((importPath) =>
				resolveRelativeModule(importPath, src)
			)
		);
		const resolvedImports = await Promise.all(
			importPaths.map((importPath) => resolveSvelte(importPath, src))
		);
		const childSources = resolvedImports.filter(
			(path): path is string => path !== null
		);
		const childBuilt = await Promise.all(
			childSources.map((child) => build(child))
		);
		const hasAwaitSlotFromChildren = childBuilt.some(
			(child) => child.hasAwaitSlot
		);
		// Build a map of original import specifiers to the correct path from the
		// generated output file. Svelte child components may point to compiled
		// siblings; plain TS/JS helpers should resolve back to their original
		// source modules because they are not separately compiled here.
		const externalRewrites = new Map<
			string,
			{ server: string; client: string }
		>();
		const ssrOutputDir = dirname(join(serverDir, relDir, `${baseName}.js`));
		const clientOutputDir = dirname(
			join(clientDir, relDir, `${baseName}.js`)
		);

		for (let idx = 0; idx < importPaths.length; idx++) {
			const rawSpec = importPaths[idx];
			if (!rawSpec) continue;
			const resolved = resolvedImports[idx];
			const resolvedModule = resolvedModuleImports[idx];

			if (!resolved && resolvedModule)
				addModuleRewrite(
					externalRewrites,
					rawSpec,
					resolvedModule,
					ssrOutputDir,
					clientOutputDir
				);
			if (!resolved) continue;

			const childRel = relative(svelteRoot, resolved).replace(/\\/g, '/');
			if (!childRel.startsWith('..')) continue;

			const childBuilt = cache.get(resolved);
			if (!childBuilt) continue;

			const origSpec = rawSpec.replace(
				/\.svelte(?:\.(?:ts|js))?$/,
				'.js'
			);
			const toServer = relative(ssrOutputDir, childBuilt.ssr).replace(
				/\\/g,
				'/'
			);
			const toClient = relative(
				clientOutputDir,
				childBuilt.client
			).replace(/\\/g, '/');

			externalRewrites.set(origSpec, {
				client: toClient.startsWith('.') ? toClient : `./${toClient}`,
				server: toServer.startsWith('.') ? toServer : `./${toServer}`
			});
		}

		const rewriteExternalImports = (
			code: string,
			mode: 'server' | 'client'
		) => {
			let result = code;

			for (const [origSpec, paths] of externalRewrites) {
				const target = mode === 'server' ? paths.server : paths.client;
				result = result.replace(origSpec, target);
			}

			return result;
		};

		const generate = (mode: 'server' | 'client') => {
			const transpiled =
				mode === 'server' ? transpiledServer : transpiledClient;
			const compiledJs = isModule
				? compileModule(transpiled, {
						dev: mode === 'client' && dev,
						experimental: {
							async: transformedByLowering
						},
						filename: src
					}).js
				: compile(transpiled, {
						css: 'injected',
						dev: mode === 'client' && dev,
						experimental: {
							async: transformedByLowering
						},
						filename: src,
						generate: mode,
						hmr: mode === 'client' && isDev
					}).js;
			let code = compiledJs.code.replace(
				/\.svelte(?:\.(?:ts|js))?(['"])/g,
				'.js$1'
			);
			// For client dev builds: replace import.meta.hot with our
			// accept registry so $.hmr() wrapper + accept callback are
			// both active. This enables component-level HMR swaps.
			if (mode === 'client' && isDev) {
				const moduleKey = `/@src/${relative(process.cwd(), src).replace(
					/\\/g,
					'/'
				)}`;
				code = code.replace(
					/if\s*\(import\.meta\.hot\)\s*\{/,
					`if (typeof window !== "undefined") {\n` +
						`  if (!window.__SVELTE_HMR_ACCEPT__) window.__SVELTE_HMR_ACCEPT__ = {};\n` +
						`  var __hmr_accept = function(cb) { window.__SVELTE_HMR_ACCEPT__[${JSON.stringify(moduleKey)}] = cb; };`
				);
				code = code.replace(
					/import\.meta\.hot\.accept\(/g,
					'__hmr_accept('
				);

				// State preservation is handled at runtime by the patched
				// $.hmr() function (collect_state/restore_state). No
				// State preservation handled by Svelte's $.hmr() runtime.
			}

			if (mode === 'client') {
				code = removeUnusedRequireHelper(code);
			}

			code += islandMetadataExports;

			return { code, map: compiledJs.map };
		};

		const ssrPath = join(serverDir, relDir, `${baseName}.js`);
		const clientPath = join(clientDir, relDir, `${baseName}.js`);

		await Promise.all([
			mkdir(dirname(ssrPath), { recursive: true }),
			mkdir(dirname(clientPath), { recursive: true })
		]);

		// Inline Svelte's compiler-emitted sourcemap on each intermediate.
		// All later transforms (`rewriteExternalImports`, the `.svelte → .js`
		// extension replace, the HMR-client wrapping for client mode) are
		// string-level edits that do not move line boundaries, so the map
		// applies directly to the written intermediate JS. `Bun.build`
		// composes this through to the final hashed bundle (with
		// `sourcemap: 'inline'`), and `chainBundleInlineSourcemap`
		// stitches the chain because Bun.build doesn't honour input
		// inline sourcemaps yet (docs/BUN_SOURCEMAP_CHAIN_BUG.md).
		const inlineMap = (map: unknown) =>
			map
				? `\n//# sourceMappingURL=data:application/json;base64,${Buffer.from(
						JSON.stringify(map)
					).toString('base64')}\n`
				: '';

		if (isModule) {
			const generated = generate('client');
			const bundle =
				rewriteExternalImports(generated.code, 'client') +
				inlineMap(generated.map);
			await Promise.all([
				write(ssrPath, bundle),
				write(clientPath, bundle)
			]);
		} else {
			const serverGen = generate('server');
			const clientGen = generate('client');
			const serverBundle =
				rewriteExternalImports(serverGen.code, 'server') +
				inlineMap(serverGen.map);
			const clientBundle =
				rewriteExternalImports(clientGen.code, 'client') +
				inlineMap(clientGen.map);
			await Promise.all([
				write(ssrPath, serverBundle),
				write(clientPath, clientBundle)
			]);
		}

		const built: Built = {
			client: clientPath,
			hasAwaitSlot:
				loweredAwaitServerSource.transformed ||
				loweredAwaitClientSource.transformed ||
				loweredServerSource.transformed ||
				loweredClientSource.transformed ||
				hasAwaitSlotFromChildren,
			isModule,
			ssr: ssrPath
		};
		cache.set(src, built);
		persistentCache.set(src, built);

		return built;
	};

	const roots = await Promise.all(entryPoints.map(build));

	// Runes modules (`.svelte.ts`) can land in the entry list during dev
	// rebuilds (the changed file itself is passed alongside its importers).
	// They compile fine as dependencies but have no component default
	// export — synthesizing a hydration index for one produces a client
	// bundle that fails with `No matching export … for import "default"`.
	const componentRoots = roots.filter((root) => !root.isModule);

	await Promise.all(
		componentRoots.map(async ({ client, hasAwaitSlot }) => {
			const relClientDir = dirname(relative(clientDir, client));
			const name = basename(client, extname(client));
			const indexPath = join(indexDir, relClientDir, `${name}.js`);
			const importRaw = relative(dirname(indexPath), client)
				.split(sep)
				.join('/');
			const importPath =
				importRaw.startsWith('.') || importRaw.startsWith('/')
					? importRaw
					: `./${importRaw}`;
			const hmrImports = isDev
				? `window.__HMR_FRAMEWORK__ = "svelte";\nimport "${hmrClientPath}";\n`
				: '';
			const bootstrap = `${hmrImports}import Component from "${importPath}";
import { hydrate, mount, unmount } from "svelte";

var initialProps = (typeof window !== "undefined" && window.__INITIAL_PROPS__) ? window.__INITIAL_PROPS__ : {};
var isHMR = typeof window !== "undefined" && window.__SVELTE_COMPONENT__ !== undefined;
var isSsrDirty = typeof window !== "undefined" && window.__SSR_DIRTY__;
var hasIslandHtml = false;
var shouldHydrate = typeof window === "undefined" ? false : ${
				hasAwaitSlot ? 'false' : 'true'
			};
var component;
var target = document.getElementById(${JSON.stringify(SVELTE_PAGE_ROOT_ID)}) || document.body;

if (typeof window !== "undefined") {
  var islandSlots = document.querySelectorAll("[data-absolute-island-slot]");
  var islandHtml = {};
  for (var index = 0; index < islandSlots.length; index += 1) {
    var slot = islandSlots[index];
    var slotId = slot.getAttribute("data-absolute-island-slot");
    if (!slotId) continue;
    islandHtml[slotId] = slot.innerHTML;
  }
  window.__ABS_SVELTE_ISLAND_HTML__ = islandHtml;
  hasIslandHtml = Object.keys(islandHtml).length > 0;
}

if (isHMR) {
  var preservedState = window.__HMR_PRESERVED_STATE__;
  if (!preservedState) {
    try {
      var stored = sessionStorage.getItem("__SVELTE_HMR_STATE__");
      if (stored) preservedState = JSON.parse(stored);
    } catch (err) { /* ignore */ }
  }
  var mergedProps = (preservedState && Object.keys(preservedState).length > 0) ? Object.assign({}, initialProps, preservedState) : initialProps;
  if (typeof window.__SVELTE_UNMOUNT__ === "function") {
    try { window.__SVELTE_UNMOUNT__(); } catch (err) { /* ignore */ }
  }
  component = mount(Component, { target, props: mergedProps });
  window.__HMR_PRESERVED_STATE__ = undefined;
} else if (!shouldHydrate) {
  component = undefined;
} else if (isSsrDirty || hasIslandHtml) {
  component = mount(Component, { target, props: initialProps });
} else {
  component = hydrate(Component, { target, props: initialProps });
}

if (typeof window !== "undefined") {
  window.__SVELTE_COMPONENT__ = component;
  window.__SVELTE_UNMOUNT__ = function() { if (component) { unmount(component); } };
  window.__SVELTE_REMOUNT__ = function(props) {
    if (typeof window.__SVELTE_UNMOUNT__ === "function") {
      try { window.__SVELTE_UNMOUNT__(); } catch (err) { /* ignore */ }
    }
    component = mount(Component, { target, props });
    window.__SVELTE_COMPONENT__ = component;
    window.__SVELTE_UNMOUNT__ = function() { if (component) { unmount(component); } };
  };
  window.__ABS_SLOT_HYDRATION_PENDING__ = shouldHydrate;
  var releaseStreamingSlots = function() {
    window.__ABS_SLOT_HYDRATION_PENDING__ = false;
    if (typeof window.__ABS_SLOT_FLUSH__ === "function") {
      window.__ABS_SLOT_FLUSH__();
    }
  };
  if (shouldHydrate && typeof requestAnimationFrame === "function") {
    requestAnimationFrame(function() {
      requestAnimationFrame(releaseStreamingSlots);
    });
  } else if (typeof window.__ABS_SLOT_FLUSH__ === "function") {
    window.__ABS_SLOT_FLUSH__();
  } else if (typeof setTimeout === "function") {
    setTimeout(releaseStreamingSlots, 0);
  }
}`;

			await mkdir(dirname(indexPath), { recursive: true });

			return write(indexPath, bootstrap);
		})
	);

	return {
		// Actual client component paths (for official HMR module imports)
		svelteClientPaths: roots.map(({ client }) => client),
		// Index paths (entry points for hydration) — component entries only;
		// runes modules have no index (see componentRoots above).
		svelteIndexPaths: componentRoots.map(({ client }) => {
			const rel = dirname(relative(clientDir, client));

			return join(indexDir, rel, basename(client));
		}),
		svelteServerPaths: roots.map(({ ssr }) => ssr)
	};
};
